import React, { useRef, useState, useCallback } from "react";
import { Alert, Progress, List, message } from "antd";
import {
  CloudUploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  FolderOutlined,
  FileOutlined,
} from "@ant-design/icons";
import IntlMessages from "@iso/components/utility/intlMessages";
import { attachment } from "@iso/services/attachment.service";

/**
 * Recursively read all files from a FileSystemDirectoryEntry.
 * Returns array of { file: File, path: "folderA/folderB/file.txt" }
 */
const readDirectoryEntry = (dirEntry, basePath = "") => {
  return new Promise((resolve) => {
    const reader = dirEntry.createReader();
    const allEntries = [];

    // readEntries may paginate (max ~100 per batch), keep reading until empty
    const readAllThenProcess = () => {
      reader.readEntries((entries) => {
        if (entries.length === 0) {
          Promise.all(allEntries.map((entry) => processEntry(entry, basePath)))
            .then((results) => resolve(results.flat()));
          return;
        }
        allEntries.push(...entries);
        readAllThenProcess();
      });
    };

    readAllThenProcess();
  });
};

const processEntry = (entry, basePath) => {
  const currentPath = basePath ? `${basePath}/${entry.name}` : entry.name;
  if (entry.isFile) {
    return new Promise((resolve) => {
      entry.file((file) => {
        resolve([{ file, path: currentPath }]);
      });
    });
  } else if (entry.isDirectory) {
    return readDirectoryEntry(entry, currentPath);
  }
  return Promise.resolve([]);
};

const UploadTab = ({ uploadProps = {}, onUploadSuccess, currentFolderId, corporateGroupId }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [isDragOver, setIsDragOver] = useState(false);

  // Core upload logic — works for both drop and file input
  const processAndUpload = useCallback(async (fileEntries) => {
    // fileEntries: [{ file: File, path: "folder/sub/file.txt" }]
    if (fileEntries.length === 0) return;

    setUploading(true);
    setUploadProgress({ done: 0, total: fileEntries.length });
    setUploadFiles(
      fileEntries.map((f) => ({ name: f.path, status: "pending" }))
    );

    try {
      // 1. Extract folder paths
      const folderPaths = new Set();
      fileEntries.forEach(({ path }) => {
        const parts = path.split("/");
        for (let i = 1; i < parts.length; i++) {
          folderPaths.add(parts.slice(0, i).join("/"));
        }
      });

      const sortedPaths = Array.from(folderPaths).sort(
        (a, b) => a.split("/").length - b.split("/").length
      );

      // 2. Create folders
      const folderIdMap = {};

      if (sortedPaths.length > 0) {
        for (const folderPath of sortedPaths) {
          const parts = folderPath.split("/");
          const folderName = parts[parts.length - 1];
          const parentPath = parts.slice(0, -1).join("/");
          const parentFolderId = parentPath
            ? folderIdMap[parentPath]
            : currentFolderId || null;

          const data = { name: folderName };
          if (corporateGroupId) data.corporate_group_id = corporateGroupId;
          if (parentFolderId) data.parent_id = parentFolderId;

          const res = await attachment.createFolder(data);
          if (res && res.code === "0000" && res.folder) {
            folderIdMap[folderPath] = res.folder.id;
          } else if (res && res.message && res.message.includes("already exists")) {
            const folderListParams = { parent_id: parentFolderId || undefined };
            if (corporateGroupId) folderListParams.corporate_group_id = corporateGroupId;
            const listRes = await attachment.getFolders(folderListParams);
            if (listRes && listRes.folders) {
              const existing = listRes.folders.find((f) => f.name === folderName);
              if (existing) folderIdMap[folderPath] = existing.id;
            }
          }
        }
      }

      // 3. Upload files
      let successCount = 0;
      let errorCount = 0;
      const uploadedAttachments = [];
      for (let i = 0; i < fileEntries.length; i++) {
        const { file, path } = fileEntries[i];
        const parts = path.split("/");
        const folderPath = parts.slice(0, -1).join("/");
        const folderId = folderIdMap[folderPath] || currentFolderId || null;

        try {
          const res = await attachment.uploadFileToFolder(file, folderId, corporateGroupId);
          if (res && res.code === "0000") {
            successCount++;
            if (res.attachment) uploadedAttachments.push(res.attachment);
          } else {
            errorCount++;
            if (res && res.message) {
              message.error(`${file.name}: ${res.message}`);
            }
          }
          setUploadProgress({ done: successCount + errorCount, total: fileEntries.length });
          setUploadFiles((prev) => {
            const updated = [...prev];
            updated[i] = {
              ...updated[i],
              status: res && res.code === "0000" ? "done" : "error",
            };
            return updated;
          });
        } catch (err) {
          errorCount++;
          setUploadProgress({ done: successCount + errorCount, total: fileEntries.length });
          setUploadFiles((prev) => {
            const updated = [...prev];
            updated[i] = { ...updated[i], status: "error" };
            return updated;
          });
        }
      }

      if (errorCount === 0) {
        message.success(`Upload complete: ${successCount} file(s) uploaded`);
      } else {
        message.warning(`Upload complete: ${successCount} succeeded, ${errorCount} failed`);
      }
      onUploadSuccess && onUploadSuccess(uploadedAttachments);
    } catch (err) {
      console.error("Upload failed:", err);
      message.error("Upload failed");
    } finally {
      setUploading(false);
    }
  }, [corporateGroupId, currentFolderId, onUploadSuccess]);

  // Handle drop — supports both files and folders
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (uploading) return;

    const items = e.dataTransfer.items;
    if (!items || items.length === 0) return;

    const fileEntries = [];

    // Use webkitGetAsEntry to detect folders vs files
    const entryPromises = [];
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry
        ? items[i].webkitGetAsEntry()
        : null;

      if (entry) {
        if (entry.isDirectory) {
          entryPromises.push(readDirectoryEntry(entry, entry.name));
        } else if (entry.isFile) {
          entryPromises.push(
            new Promise((resolve) => {
              entry.file((file) => resolve([{ file, path: file.name }]));
            })
          );
        }
      } else {
        // Fallback: treat as regular file
        const file = items[i].getAsFile();
        if (file) {
          fileEntries.push({ file, path: file.name });
        }
      }
    }

    const results = await Promise.all(entryPromises);
    results.forEach((arr) => fileEntries.push(...arr));

    if (fileEntries.length > 0) {
      processAndUpload(fileEntries);
    }
  }, [uploading, processAndUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  // Click to browse — uses regular file input (multi-select)
  const handleClick = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    // Check if files have webkitRelativePath (folder was selected)
    const hasRelativePath = files.some(
      (f) => f.webkitRelativePath && f.webkitRelativePath.includes("/")
    );

    const fileEntries = files.map((f) => ({
      file: f,
      path: hasRelativePath ? f.webkitRelativePath : f.name,
    }));

    processAndUpload(fileEntries);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "done":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "error":
        return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
      case "pending":
        return <LoadingOutlined style={{ color: "#999" }} />;
      default:
        return null;
    }
  };

  const getItemIcon = (name) => {
    const parts = name.split("/");
    return parts.length > 1 ? (
      <FolderOutlined style={{ color: "#faad14", marginRight: 4 }} />
    ) : (
      <FileOutlined style={{ color: "#999", marginRight: 4 }} />
    );
  };

  return (
    <div className="upload-tab-content">
      <Alert
        className="upload-hint"
        message={<IntlMessages id="fileBrowser.uploadHint" />}
        type="info"
        showIcon
      />

      {/* Unified drop zone */}
      <div
        className={`upload-drop-zone ${isDragOver ? "drag-over" : ""} ${uploading ? "uploading" : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />
        <CloudUploadOutlined className="drop-zone-icon" />
        <p className="drop-zone-title">
          {uploading ? "Uploading..." : "Drop files or folders here"}
        </p>
        <p className="drop-zone-subtitle">
          or click to browse files
        </p>
      </div>

      {/* Upload progress */}
      {uploadFiles.length > 0 && (
        <div className="folder-upload-progress">
          <Progress
            percent={
              uploadProgress.total > 0
                ? Math.round((uploadProgress.done / uploadProgress.total) * 100)
                : 0
            }
            size="small"
            status={uploading ? "active" : undefined}
          />
          <List
            className="folder-upload-list"
            size="small"
            dataSource={uploadFiles}
            renderItem={(item) => (
              <List.Item>
                {getStatusIcon(item.status)}
                {getItemIcon(item.name)}
                <span className="folder-upload-filename">{item.name}</span>
              </List.Item>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default UploadTab;
